import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js"
import { loadStripe, StripeCardElement, StripeElementsOptions } from "@stripe/stripe-js"

import style from "./StripeFieldSet.module.scss"

export type StripeFieldSetProps = {
  disabled: boolean
  amountInCents: number
  currency?: "usd" | "cad" | "hkd" | "eur" | "gbp"
  tokenFieldName?: string
  formId?: string
  submitButtonId?: string
}

function logError(...errorArgs: unknown[]) {
  if (import.meta.env.PROD) {
    // @ts-expect-error Rollbar is loaded in a head script tag
    window?.Rollbar?.error(...errorArgs)
  } else {
    console.error(...errorArgs)
  }
}

/// StripeFieldSet - Provides a Stripe-powered credit card form. Strongly coupled to our legacy checkout/payment flows.
/// WARN: This component works by intercepting form submission. If the form detects it was hidden (via the checkVisibility api),
///       the component will be disabled and not intercept form submission.
export function StripeFieldSet(props: StripeFieldSetProps) {
  const {
    disabled,
    amountInCents,
    currency = "usd",
    tokenFieldName = "stripe_card_token",
    formId = "pay_store_order",
    submitButtonId = "payment_submit_button",
  } = props

  const options: StripeElementsOptions = {
    mode: "payment",
    currency,
    amount: amountInCents,
  }

  const stripePromise = useMemo(() => {
    // We store the Stripe key in a meta tag
    const stripeKeyMeta = document.querySelector("meta[name=stripe-key]")

    if (!(stripeKeyMeta instanceof HTMLMetaElement)) return null

    return loadStripe(stripeKeyMeta.content)
  }, [])

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm
        tokenFieldName={tokenFieldName}
        formId={formId}
        disabled={disabled}
        submitButtonId={submitButtonId}
      />
    </Elements>
  )
}

const CheckoutForm = ({
  tokenFieldName,
  formId,
  disabled,
  submitButtonId,
}: {
  tokenFieldName: string
  formId: string
  disabled: boolean
  submitButtonId: string
}) => {
  const [error, setError] = useState<string | undefined>()
  const elements = useElements()
  const stripe = useStripe()
  const ref = useRef<HTMLDivElement | null>(null)

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      // If the form is hidden, we don't hijack form submission here
      // There are various flows that hide the form (applying promo codes that zero out cost, using other saved cards, etc)- this is a failsafe catch-all.
      // Without it, if the form is hidden, submission will break for the user by silently preventing form submission
      if (disabled || !ref.current?.checkVisibility()) {
        return
      }
      event.preventDefault()

      // Disable the submit button
      const submitButton = document.getElementById(submitButtonId) as HTMLButtonElement
      if (submitButton) {
        submitButton.disabled = true
      }

      const cardEl = elements?.getElement("card")
      const formEl = event.target
      const response = await stripe?.createToken(cardEl as StripeCardElement)

      // If there is an error, we need to re-enable the submit button
      if (!response || response?.error) {
        if (submitButton) {
          submitButton.disabled = false
        }
      }

      if (!response) {
        logError(new Error("No response when attempting to create a stripe token"))
        return
      }

      if (response?.error) {
        setError(response.error.message)
        if (response.error.type != "validation_error") {
          logError(new Error(`${response.error.message} (${response.error.type})`))
        }
        return
      }

      setError(undefined)

      if (!(formEl instanceof HTMLFormElement)) return

      const tokenInput = document.createElement("input")
      tokenInput.type = "hidden"
      tokenInput.name = tokenFieldName
      tokenInput.value = response?.token?.id ?? ""

      formEl.appendChild(tokenInput)

      requestAnimationFrame(() => formEl.submit())
    },
    [stripe, disabled]
  )

  useEffect(() => {
    const formEl = document.querySelector(`form#${formId}`)

    if (!(formEl instanceof HTMLFormElement)) return

    formEl?.addEventListener("submit", handleSubmit)

    return () => {
      formEl?.removeEventListener("submit", handleSubmit)
    }
  }, [handleSubmit])

  return (
    <div style={{ display: "content" }} ref={ref}>
      <CardElement
        className={style["stripe-ada"]}
        onChange={({ error }) => {
          if (error) {
            setError(error.message)
          } else {
            setError(undefined)
          }
        }}
        options={{ hidePostalCode: true }}
      />
      {error ? <div style={{ color: "red" }}>{error}</div> : <div style={{ visibility: "hidden" }}>&nbsp;</div>}
    </div>
  )
}
